import { Edit, Plus } from "@neonumy/neonumy-icons";
import { Button, Table } from "@neonumy/neonumy-ui";
import { Space, Spin } from "antd5";
import PageTitle from "components/PageTitle";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CreateUpdateUserAccess from "./CreateUpdateUserAccess";
import DeleteUserAccess from "./DeleteUserAccess";
import { useUserAccess } from "./hooks/useUserAccess";

/**
 * This component manages user access to accounting firms.
 * It displays a table with the list of accounting firms the user has access to,
 * and provides options to create, update, and delete user access.
 * @returns A React component that renders the user access management interface.
 *
 * @example
 * <UserAccess userId={123} />
 */
export default function UserAccess({ userId }: { userId: ID }) {
  const { t } = useTranslation();
  const { dataSource, refetch, loading, accountingFirmsId } = useUserAccess({ userId });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccountingFirmId, setSelectedAccountingFirmId] = useState<ID>();

  // Add passes no id, so the same modal serves both.
  const openModal = (accountingFirmId?: ID) => {
    setSelectedAccountingFirmId(accountingFirmId);
    setIsModalOpen(true);
  };

  const columns = [
    {
      dataIndex: "accountingFirm",
      title: t("Accounting firm"),
    },
    {
      key: "actions",
      width: "100px",
      render: (_name: string, record: ListAccountingFirmsAccess) => (
        <Space direction="horizontal" size="small">
          <Button
            icon={<Edit />}
            type="text"
            color="newrai-blue"
            onClick={() => openModal(record.id)}
          />
          <DeleteUserAccess accountingFirmId={record.id} userId={userId} refetch={refetch} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title={t("User accesses")}
        className="narrow-table-width"
        table
        actions={
          <Button
            variant="outlined"
            bold
            icon={<Plus />}
            iconPosition="start"
            onClick={() => openModal()}
          >
            {t("Add")}
          </Button>
        }
      />
      <Spin spinning={loading}>
        <Table
          totalLabel={t("Accesses")}
          className="narrow-table-width"
          actionsColumn="inside"
          columns={columns}
          dataSource={dataSource}
        />
      </Spin>
      <CreateUpdateUserAccess
        open={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        userId={userId}
        accountingFirmId={selectedAccountingFirmId}
        accountingFirmsId={accountingFirmsId.current}
        refetch={refetch}
      />
    </>
  );
}
